import format from "comma-number";
import Link from "next/link";
import useSWR from "swr";
import InteractiveBadge from "~/components/InteractiveBadge";
import { apiUrl } from "~/config";
import type { PostCard } from "~/lib/blog";
import fetcher from "~/lib/fetcher";

type Props = {
  post: PostCard;
  /** The section the post lives in: "blog", or "inner-circle". */
  blogUrl?: string;
  views?: number | null;
  fetchViews?: boolean;
};

/**
 * The one way a post is listed: on the home page, the blog index and the inner circle alike. It takes the post
 * whole, so its tags (Draft, Interactive) show wherever it does. Fetches the view count itself unless `views` is
 * given, or `fetchViews` is false because the list fetches every count at once. Counts come from `apiUrl`, the
 * inner circle's too. Drafts never fetch views.
 */
const BlogPost = ({
  post: { slug, title, summary, isDraft = false, interactive = false },
  blogUrl = "blog",
  views = null,
  fetchViews = true
}: Props) => {
  const { data } = useSWR(
    !isDraft && views == null && fetchViews && slug ? `${apiUrl}/api/${blogUrl}/${blogUrl}/${slug}` : null,
    fetcher
  );
  views = data?.total ?? views;

  return (
    <div className="mb-8 w-full">
      <div className="flex flex-col md:flex-row justify-between">
        <h4 className="underline text-lg md:text-xl font-medium mb-2 w-full text-gray-900 dark:text-gray-100">
          <Link href={`/${isDraft ? "_draft/blog" : blogUrl}/${slug}`} className="no-underline w-full">
            {isDraft && (
              <span className="mr-2 rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-900 dark:text-amber-100">
                Draft
              </span>
            )}
            {interactive && <InteractiveBadge />}
            {title}
          </Link>
        </h4>
        {!isDraft && (
          <p className="text-gray-500 text-left md:text-right w-32 mb-4 md:mb-0">
            {`${views != null ? format(views) : "–––"} views`}
          </p>
        )}
      </div>
      <p className="text-gray-600 dark:text-gray-400">{summary}</p>
    </div>
  );
};

export default BlogPost;
